import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import type {
  IMergedFloorView,
  IMinimizedProfession,
  IDashboardData,
  IActiveManager,
  IProfession,
  IProfessionWithActiveTags,
  IUtilityMergedTag,
  IMainPageWidgetActivityData,
  IMinimalSafetyEventForClient,
  IMergedEquipmentView,
  IProject,
  ITrusstorUserPermissions,
  EquipmentTypes,
} from '@d-orca/shared/trusstor';
import type { MonitorSnapshot, MonitorStreamEvent } from '@interfaces/IMonitorApi';
import type {
  ActivityFilter,
  IssueFilter,
  MonitorLayer,
} from '@components/Pages/MonitorPage/layers/layers';

export type MonitorTab = 'workforce' | 'safety';
export type SnapshotStatus = 'idle' | 'loading' | 'ready' | 'error';
export type StreamStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

export type MonitorState = {
  siteId: string | null;
  project: IProject | null;
  floors: IMergedFloorView<IMinimizedProfession>[];
  dashboardData: IDashboardData | null;
  managers: IActiveManager[];
  professions: IProfession[];
  professionsWithActiveTags: IProfessionWithActiveTags<IMinimizedProfession>[];
  utilities: IUtilityMergedTag<IMinimizedProfession>[];
  activities: IMainPageWidgetActivityData<IMinimizedProfession>[];
  safetyEvents: IMinimalSafetyEventForClient[];
  staticEquipment: IMergedEquipmentView[];
  activeEquipment: IMergedEquipmentView[];
  /** Static worker-tag roster (REST snapshot field) — powers the operations
   *  panels' planned-headcount / certification figures. */
  staticTags: NonNullable<MonitorSnapshot['staticTags']>;
  userPermissions: ITrusstorUserPermissions | null;
  /** FloorIds the BFF says should appear in the building view (from Trusstor's project SVG). null = no whitelist. */
  displayFloorIds: string[] | null;
  snapshotStatus: SnapshotStatus;
  streamStatus: StreamStatus;
  selectedFloorId: string | null;
  activeTab: MonitorTab;
  /** Which chips the building draws, and which list the rail shows. */
  layer: MonitorLayer;
  activityFilter: ActivityFilter;
  issueFilter: IssueFilter;
  /**
   * LeftRail filter sets. Empty array = no filter for that category (show all);
   * non-empty = whitelist (show only these). Mirrors trusstor_dashboard's
   * `selectedProfessions` / `selectedManagers` / `selectedStaticEquipment`
   * semantics from `professions.slice` / `managers.slice` / `equipment.slice`.
   */
  selectedProfessionIds: string[];
  selectedManagerIds: string[];
  selectedEquipmentTypes: EquipmentTypes[];
};

const initialState: MonitorState = {
  siteId: null,
  project: null,
  floors: [],
  dashboardData: null,
  managers: [],
  professions: [],
  professionsWithActiveTags: [],
  utilities: [],
  activities: [],
  safetyEvents: [],
  staticEquipment: [],
  activeEquipment: [],
  staticTags: [],
  userPermissions: null,
  displayFloorIds: null,
  snapshotStatus: 'idle',
  streamStatus: 'closed',
  selectedFloorId: null,
  activeTab: 'workforce',
  layer: 'workforce',
  activityFilter: 'all',
  issueFilter: 'all',
  selectedProfessionIds: [],
  selectedManagerIds: [],
  selectedEquipmentTypes: [],
};

/** Folds one stream event into the monitor state. */
function applyEvent(state: Draft<MonitorState>, ev: MonitorStreamEvent): void {
  switch (ev.channel) {
    case 'floors': {
      const mergedFloors = ev.data.mergedFloors ?? [];
      if (ev.data.shouldReplace) {
        state.floors = mergedFloors;
      } else {
        const byId = new Map(state.floors.map((f) => [f.floorId, f]));
        for (const next of mergedFloors) byId.set(next.floorId, next);
        state.floors = Array.from(byId.values());
      }
      break;
    }
    case 'dashboardData': {
      // Trusstor's dashboardData/stream emits `{ dashboardData }` where
      // managers/professionsWithActiveTags/utilities are nested inside the
      // inner `dashboardData` object — but d-orca's contract may also
      // surface them at the top level. Read from both safely; never
      // overwrite an existing array with undefined.
      const inner = (ev.data.dashboardData ?? null) as
        | (typeof ev.data.dashboardData & {
            managers?: typeof ev.data.managers;
            professionsWithActiveTags?: typeof ev.data.professionsWithActiveTags;
            utilities?: typeof ev.data.utilities;
          })
        | null;
      state.dashboardData = inner;
      const nextManagers = ev.data.managers ?? inner?.managers;
      const nextPwa = ev.data.professionsWithActiveTags ?? inner?.professionsWithActiveTags;
      const nextUtilities = ev.data.utilities ?? inner?.utilities;
      if (nextManagers) state.managers = nextManagers;
      if (nextPwa) state.professionsWithActiveTags = nextPwa;
      if (nextUtilities) state.utilities = nextUtilities;
      break;
    }
    case 'activities': {
      state.activities = ev.data ?? [];
      break;
    }
    case 'safety': {
      // Some Trusstor safety SSE shapes emit a bare array; tolerate both.
      const data = ev.data as unknown;
      let incoming: typeof state.safetyEvents | null = null;
      if (Array.isArray(data)) {
        incoming = data as typeof state.safetyEvents;
      } else if (
        data &&
        typeof data === 'object' &&
        Array.isArray((data as { safetyEvents?: unknown }).safetyEvents)
      ) {
        incoming = (data as { safetyEvents: typeof state.safetyEvents }).safetyEvents;
      }
      if (!incoming) break;
      // Trusstor's `/safety/stream` only sends the events that just fired
      // or just closed — never the full set after the initial snapshot
      // (see SafetyEventsBroadcastService.sendSafetyEventsToClient). A
      // wholesale replace would shrink the list to whichever single
      // event arrived last. Merge by `_id` with incoming winning so a
      // close-status push updates the matching active entry's status.
      // Mirrors trusstor_dashboard's safetyEvents.slice.ts:38
      // (`uniqBy([...incoming, ...state], '_id')`).
      const seen = new Set<string>();
      const merged: typeof state.safetyEvents = [];
      for (const e of incoming) {
        if (!seen.has(e._id)) {
          seen.add(e._id);
          merged.push(e);
        }
      }
      for (const e of state.safetyEvents) {
        if (!seen.has(e._id)) {
          seen.add(e._id);
          merged.push(e);
        }
      }
      state.safetyEvents = merged;
      break;
    }
    case '_heartbeat':
    case '_resync':
      break;
  }
}

const monitorSlice = createSlice({
  name: 'monitor',
  initialState,
  reducers: {
    resetForSite(state, action: PayloadAction<string>) {
      Object.assign(state, initialState, { siteId: action.payload });
    },
    setSnapshotStatus(state, action: PayloadAction<SnapshotStatus>) {
      state.snapshotStatus = action.payload;
    },
    setStreamStatus(state, action: PayloadAction<StreamStatus>) {
      state.streamStatus = action.payload;
    },
    applySnapshot(state, action: PayloadAction<MonitorSnapshot>) {
      const s = action.payload;
      state.project = s.project;
      // Snapshot only carries the fields Trusstor exposes via REST. Live-only
      // fields (floors, dashboardData, managers, professionsWithActiveTags,
      // utilities, activities, safetyEvents) come exclusively from SSE — do
      // NOT overwrite them here, because the SSE first event often arrives
      // before this snapshot resolves and we'd clobber populated state with
      // the BFF's empty stubs.
      state.professions = s.professions ?? state.professions;
      state.staticTags = s.staticTags ?? state.staticTags;
      state.staticEquipment = s.staticEquipment ?? state.staticEquipment;
      state.activeEquipment = s.activeEquipment ?? state.activeEquipment;
      state.userPermissions = s.userPermissions ?? state.userPermissions;
      state.displayFloorIds = s.displayFloorIds ?? state.displayFloorIds;
      state.snapshotStatus = 'ready';
    },
    applyStreamEvent(state, action: PayloadAction<MonitorStreamEvent>) {
      applyEvent(state, action.payload);
    },
    /** A burst of stream events folded in one update, so the page renders once
     *  rather than once per event (Trusstor pushes each floor on its own). */
    applyStreamEvents(state, action: PayloadAction<MonitorStreamEvent[]>) {
      for (const ev of action.payload) applyEvent(state, ev);
    },
    setSelectedFloor(state, action: PayloadAction<string | null>) {
      state.selectedFloorId = action.payload;
    },
    setActiveTab(state, action: PayloadAction<MonitorTab>) {
      state.activeTab = action.payload;
    },
    setLayer(state, action: PayloadAction<MonitorLayer>) {
      state.layer = action.payload;
    },
    setActivityFilter(state, action: PayloadAction<ActivityFilter>) {
      state.activityFilter = action.payload;
    },
    setIssueFilter(state, action: PayloadAction<IssueFilter>) {
      state.issueFilter = action.payload;
    },
    setProfessionFilters(state, action: PayloadAction<string[]>) {
      state.selectedProfessionIds = action.payload;
    },
    setManagerFilters(state, action: PayloadAction<string[]>) {
      state.selectedManagerIds = action.payload;
    },
    setEquipmentFilters(state, action: PayloadAction<EquipmentTypes[]>) {
      state.selectedEquipmentTypes = action.payload;
    },
  },
});

export const {
  resetForSite,
  setSnapshotStatus,
  setStreamStatus,
  applySnapshot,
  applyStreamEvent,
  applyStreamEvents,
  setSelectedFloor,
  setActiveTab,
  setLayer,
  setActivityFilter,
  setIssueFilter,
  setProfessionFilters,
  setManagerFilters,
  setEquipmentFilters,
} = monitorSlice.actions;

export const monitorReducer = monitorSlice.reducer;
